import DOMPurify from "dompurify";
import katex from "katex";
import { convertEol, normalizeToLf, type EolStyle } from "../doc/eol.js";
import { escapeHtml } from "./escapeHtml.js";
import { hljs } from "./hljsRegister.js";

/** Optional base (e.g. deployed API); default same-origin `/api/oeis/`. */
function oeisApiBase(): string {
  const v = import.meta.env.VITE_OEIS_API_BASE as string | undefined;
  return v?.trim() ? v.replace(/\/$/, "") : "";
}

/** JSON search URL for an A-number (proxied in dev / nginx to oeis.org). */
export function oeisSearchJsonUrl(anumber: string): string {
  const q = `id:${anumber}`;
  return oeisSearchJsonUrlForRawQuery(q);
}

/** JSON search URL with arbitrary OEIS `q` (e.g. comma-separated terms, `id:A…`). `start` paginates (step 10). */
export function oeisSearchJsonUrlForRawQuery(q: string, start = 0): string {
  const base = oeisApiBase();
  let path = `/search?fmt=json&q=${encodeURIComponent(q)}`;
  if (start > 0) path += `&start=${encodeURIComponent(String(start))}`;
  return base ? `${base}${path}` : `/api/oeis${path}`;
}

const ANUM_RE = /\b(A\d+)\b/i;
const URL_ANUM_RE = /oeis\.org\/(A\d+)/i;

function normalizeOeisAnumber(id: string): string {
  const m = id.match(/^A(\d+)$/i);
  if (!m?.[1]) return id.toUpperCase();
  const digits = m[1];
  const canonical = digits.length < 6 ? digits.padStart(6, "0") : digits;
  return `A${canonical}`;
}

/** Pick canonical `A123456` from buffer (first line, URL, or first match). */
export function extractOeisAnumber(text: string): string | null {
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const direct = line.match(/^(A\d+)$/i);
    if (direct?.[1]) return normalizeOeisAnumber(direct[1]);
    const fromUrl = line.match(URL_ANUM_RE);
    if (fromUrl?.[1]) return normalizeOeisAnumber(fromUrl[1]);
  }
  const any = text.match(ANUM_RE);
  return any?.[1] ? normalizeOeisAnumber(any[1]) : null;
}

/** Parse A-number from toolbar input: bare `A000045`, `a000045`, or an `oeis.org/.../A000045` URL. */
export function parseOeisSeqIdFromInput(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const dir = t.match(/^(A\d+)$/i);
  if (dir?.[1]) return normalizeOeisAnumber(dir[1]);
  const url = t.match(URL_ANUM_RE);
  if (url?.[1]) return normalizeOeisAnumber(url[1]);
  const any = t.match(/\b(A\d+)\b/i);
  return any?.[1] ? normalizeOeisAnumber(any[1]) : null;
}

/** Public OEIS site hostnames (links in preview use these). */
export function isOeisOrgHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === "oeis.org" || h.endsWith(".oeis.org");
}

/**
 * Map `https://oeis.org/...` to same-origin `/api/oeis/...` for fetches (avoids CORS on raw oeis.org).
 */
export function oeisOrgHttpUrlToApiProxyPath(href: string): string | null {
  try {
    const u = new URL(href);
    if (!isOeisOrgHostname(u.hostname)) return null;
    return `/api/oeis${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

/**
 * If `href` points at oeis.org and a path/query segment yields an A-number, return canonical id.
 * Used for “open sequence in new OEIS tab”; callers should treat trailing `.txt` separately first.
 */
export function oeisSequenceIdFromOeisOrgHttpUrl(href: string): string | null {
  try {
    const u = new URL(href);
    if (!isOeisOrgHostname(u.hostname)) return null;
    for (const seg of u.pathname.split("/").filter(Boolean)) {
      const id = parseOeisSeqIdFromInput(seg);
      if (id) return id;
    }
    return (
      parseOeisSeqIdFromInput(`${u.pathname}${u.search}`) ?? parseOeisSeqIdFromInput(href)
    );
  } catch {
    return parseOeisSeqIdFromInput(href);
  }
}

export function urlPathnameLooksLikeDownloadedHtml(pathname: string): boolean {
  return /\.html?$|\.xhtml$/i.test(pathname);
}

export function urlPathnameLooksLikeDownloadedPlainText(pathname: string): boolean {
  return /\.txt$|\.text$/i.test(pathname);
}

/** Preview: open the system browser instead of a NullaPad tab for HTML-style page links. */
export function previewHyperlinkOpensHtmlInExternalBrowser(href: string): boolean {
  try {
    const u = new URL(href);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const pathname = u.pathname;
    const isOeis = isOeisOrgHostname(u.hostname);
    if (isOeis && urlPathnameLooksLikeDownloadedPlainText(pathname)) return false;
    if (oeisSequenceIdFromOeisOrgHttpUrl(u.href)) return false;
    return (
      urlPathnameLooksLikeDownloadedHtml(pathname) ||
      (isOeis && !urlPathnameLooksLikeDownloadedPlainText(pathname))
    );
  } catch {
    return false;
  }
}

const INT_TOKEN_RE = /-?\d+/g;

/** Minimum integer terms required to run an OEIS lookup-by-terms search. */
export const OEIS_TERMS_SEARCH_MIN_TERMS = 3;

/** Maximum terms sent in a lookup-by-terms query (OEIS accepts long lists; cap for URL size). */
export const OEIS_TERMS_SEARCH_MAX_TERMS = 120;

function oeisAnumberFromEntryId(raw: string | undefined): string | null {
  if (!raw) return null;
  const m = raw.match(/\b(A\d+)\b/i);
  return m?.[1] ? normalizeOeisAnumber(m[1]) : null;
}

/**
 * Canonical A-number from one OEIS `fmt=json` **search** row. Hits often use legacy `id` strings such as
 * `M0692 N0256` with no `A…` substring; the JSON `number` field is then the catalog index (same as in
 * `https://oeis.org/A000045` for `number` 45).
 */
export function oeisAnumberFromSearchHit(row: OeisJsonEntry): string | null {
  const fromId = oeisAnumberFromEntryId(row.id);
  if (fromId) return fromId;
  const n = row.number;
  if (typeof n === "number" && Number.isFinite(n) && n > 0) {
    return normalizeOeisAnumber(`A${Math.trunc(n)}`);
  }
  return null;
}

/** Merge OEIS search pages in order, skipping duplicate canonical A-numbers. */
export function mergeOeisSearchHitRows(
  existing: readonly OeisJsonEntry[],
  more: readonly OeisJsonEntry[],
): OeisJsonEntry[] {
  const seen = new Set<string>();
  const out: OeisJsonEntry[] = [];
  for (const r of [...existing, ...more]) {
    const id = oeisAnumberFromSearchHit(r);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

/**
 * Parse a comma/space-separated list of signed integers from one line of text (toolbar / buffer).
 * Returns `null` if fewer than {@link OEIS_TERMS_SEARCH_MIN_TERMS} finite integers were found.
 */
export function parseOeisTermsSearchFromInput(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const hits = t.match(INT_TOKEN_RE);
  if (!hits?.length) return null;
  const nums: number[] = [];
  for (const h of hits) {
    const n = Number(h);
    if (Number.isFinite(n)) nums.push(n);
    if (nums.length >= OEIS_TERMS_SEARCH_MAX_TERMS) break;
  }
  if (nums.length < OEIS_TERMS_SEARCH_MIN_TERMS) return null;
  return nums.join(",");
}

/**
 * Treat the first non-empty line as a terms query when it is not an A-number/URL line and parses
 * via {@link parseOeisTermsSearchFromInput}.
 */
export function extractOeisTermsSearchQuery(text: string): string | null {
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (extractOeisAnumber(line) !== null) return null;
    return parseOeisTermsSearchFromInput(line);
  }
  return null;
}

export type OeisPreviewQuery =
  | { mode: "id"; id: string }
  | { mode: "terms"; query: string };

/**
 * How the OEIS preview should fetch: **only the first non-empty line** of the buffer decides between
 * `id:A…` lookup and comma-separated terms search. Later lines (notes, crossrefs) must not override
 * a terms-first buffer — {@link extractOeisAnumber} scans the whole document and would break that.
 */
export function resolveOeisPreviewQuery(text: string): OeisPreviewQuery | null {
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const bare = line.match(/^(A\d+)$/i);
    if (bare?.[1]) return { mode: "id", id: normalizeOeisAnumber(bare[1]) };
    const fromUrl = line.match(URL_ANUM_RE);
    if (fromUrl?.[1]) return { mode: "id", id: normalizeOeisAnumber(fromUrl[1]) };
    const lead = line.match(/^\s*(A\d+)\b/i);
    if (lead?.[1]) return { mode: "id", id: normalizeOeisAnumber(lead[1]) };
    const terms = parseOeisTermsSearchFromInput(line);
    if (terms) return { mode: "terms", query: terms };
    return null;
  }
  return null;
}

/**
 * Put `id` at the start of the OEIS buffer: replace the first non-empty line if it already yields an
 * A-number via {@link extractOeisAnumber}; otherwise insert `id` before that line. Respects document EOL.
 */
export function applyOeisSequenceIdToSource(editorText: string, id: string, eol: EolStyle): string {
  const core = normalizeToLf(editorText);
  const lines = core.split("\n");
  const firstIdx = lines.findIndex((l) => l.trim() !== "");
  if (firstIdx === -1) {
    return convertEol(id, eol);
  }
  const first = lines[firstIdx]!;
  if (extractOeisAnumber(first) !== null) {
    lines[firstIdx] = id;
  } else {
    lines.splice(firstIdx, 0, id);
  }
  return convertEol(lines.join("\n"), eol);
}

/**
 * Put a lookup-by-terms query line at the start: replaces the first non-empty line when it is already
 * a terms query (no A-number on that line); otherwise inserts before that line.
 */
export function applyOeisTermsSearchLineToSource(editorText: string, termsCsv: string, eol: EolStyle): string {
  const core = normalizeToLf(editorText);
  const lines = core.split("\n");
  const firstIdx = lines.findIndex((l) => l.trim() !== "");
  if (firstIdx === -1) {
    return convertEol(termsCsv, eol);
  }
  const first = lines[firstIdx]!;
  if (extractOeisAnumber(first) === null && parseOeisTermsSearchFromInput(first) !== null) {
    lines[firstIdx] = termsCsv;
  } else {
    lines.splice(firstIdx, 0, termsCsv);
  }
  return convertEol(lines.join("\n"), eol);
}

export type OeisJsonEntry = {
  number?: number;
  id?: string;
  data?: string;
  name?: string;
  comment?: string | string[];
  formula?: string | string[];
  example?: string | string[];
  keyword?: string | string[];
  xref?: string | string[];
  link?: string | string[];
  reference?: string | string[];
  references?: string | string[];
  maple?: string | string[];
  mathematica?: string | string[];
  program?: string | string[];
  offset?: string | string[];
  author?: string | string[];
  created?: string;
  time?: string;
  revision?: string | number;
};

export function parseOeisSearchJsonRows(data: unknown): OeisJsonEntry[] {
  if (!Array.isArray(data) || data.length === 0) return [];
  const out: OeisJsonEntry[] = [];
  for (const row of data) {
    if (typeof row === "object" && row !== null) out.push(row as OeisJsonEntry);
  }
  return out;
}

export function parseOeisSearchJson(data: unknown): OeisJsonEntry | null {
  const rows = parseOeisSearchJsonRows(data);
  return rows[0] ?? null;
}

export function parseTermsData(data: string): number[] {
  const parts = data.split(/,/g);
  const out: number[] = [];
  for (const p of parts) {
    const n = Number(String(p).trim());
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

/** Simple SVG polyline of the first terms (min–max scaled). */
export function termsSparklineSvg(values: readonly number[], width = 400, height = 96): string {
  const slice = values.slice(0, 160);
  if (slice.length < 2) return "";
  let min = Math.min(...slice);
  let max = Math.max(...slice);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = 6;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const pts = slice
    .map((v, i) => {
      const x = pad + (i / (slice.length - 1)) * w;
      const y = pad + (1 - (v - min) / (max - min)) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    `<svg class="npp-oeis-sparkline" xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}" role="img" aria-label="First terms trend"><polyline fill="none" ` +
    `stroke="#1a56b8" stroke-width="2" points="${pts}"/></svg>`
  );
}

function fullStringList(x: string | string[] | undefined): string[] {
  if (x === undefined) return [];
  return (Array.isArray(x) ? x : [x]).map((s) => String(s));
}

/** One list item per JSON entry; split `;` when OEIS packs several attributions in one string. */
function expandOeisAuthorEntries(items: readonly string[]): string[] {
  const out: string[] = [];
  for (const raw of items) {
    const t = raw.trim();
    if (!t) continue;
    const parts = t.split(/\s*;\s*/).map((p) => p.trim()).filter((p) => p.length > 0);
    out.push(...parts);
  }
  return out;
}

function normalizeOeisCreditVerb(verb: string): string {
  const v = verb.toLowerCase();
  return v.length ? v.charAt(0).toUpperCase() + v.slice(1) : verb;
}

/** OEIS sometimes drops the leading “C” (“orrected by …”). */
function canonicalCreditVerbWord(word: string): string {
  const u = word.toLowerCase();
  if (u === "orrected") return "Corrected";
  return normalizeOeisCreditVerb(word);
}

const OEIS_BYLINE_VERB = String.raw`(?:C?orrected|Updated|Extended|Edited|Revised)`;

/** OEIS sometimes wraps editorial lines in `[ ... ]`. */
function peelOptionalOuterBrackets(line: string): string {
  const t = line.trim();
  if (t.length >= 2 && t.startsWith("[") && t.endsWith("]")) {
    return t.slice(1, -1).trim();
  }
  return t;
}

/**
 * OEIS `author` values often look like ` - _Gary W. Adamson_, Mar 08 2009` or `_Name_, date`.
 * Comment tails may use `Corrected by _Name_, Jul 29 2025` or `[Corrected by _Name_, Jul 29 2025]`.
 */
export function formatOeisAuthorLineHtml(raw: string): string {
  const t = peelOptionalOuterBrackets(raw);
  const normalizeCompactAuthorName = (nameRaw: string): string =>
    nameRaw
      .replace(/([A-Za-z])[−–—-]([A-Za-z])/g, "$1-$2")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Za-z])and(\s+[A-Z])/g, "$1 and$2")
      .trim();
  const tableFrom = t.match(/^\[?\s*table\s+from\s+([^[\].]+?)\s*\]?\s*\.?\s*$/i);
  if (tableFrom?.[1]) {
    const name = tableFrom[1].trim();
    return (
      `<span class="npp-oeis-author-sig npp-oeis-author-sig--verb">` +
      `<span class="npp-oeis-author-verb">Table from </span>` +
      `<span class="npp-oeis-author-name">${escapeHtml(name)}</span>` +
      `</span>`
    );
  }
  const byM = t.match(new RegExp(`^(${OEIS_BYLINE_VERB})\\s+by\\s+_([^_]+)_\\s*,\\s*(.+)$`, "i"));
  if (byM?.[1] !== undefined && byM[2] !== undefined && byM[3] !== undefined) {
    const verb = canonicalCreditVerbWord(byM[1]);
    const name = byM[2].trim();
    const tail = byM[3].trim();
    return (
      `<span class="npp-oeis-author-sig npp-oeis-author-sig--verb">` +
      `<span class="npp-oeis-author-verb">${escapeHtml(verb)} by </span>` +
      `<span class="npp-oeis-author-name">${escapeHtml(name)}</span>` +
      `<span class="npp-oeis-author-date">, ${escapeHtml(tail)}</span>` +
      `</span>`
    );
  }
  const m = t.match(/^(?:-\s+)?_([^_]+)_\s*,\s*(.+)$/);
  if (m?.[1] !== undefined && m[2] !== undefined) {
    const name = m[1].trim();
    const tail = m[2].trim();
    return (
      `<span class="npp-oeis-author-sig">` +
      `<span class="npp-oeis-author-name">${escapeHtml(name)}</span>` +
      `<span class="npp-oeis-author-date">, ${escapeHtml(tail)}</span>` +
      `</span>`
    );
  }
  const plainDashByline = t.match(/^(?:-|–|—|−)\s*([^,]+?)\s*,\s*(.+)$/);
  if (plainDashByline?.[1] !== undefined && plainDashByline[2] !== undefined) {
    const name = normalizeCompactAuthorName(plainDashByline[1]);
    const tail = plainDashByline[2].trim();
    return (
      `<span class="npp-oeis-author-sig">` +
      `<span class="npp-oeis-author-name">${escapeHtml(name)}</span>` +
      `<span class="npp-oeis-author-date">, ${escapeHtml(tail)}</span>` +
      `</span>`
    );
  }
  return escapeHtml(t);
}

/**
 * Leading `From _Author_, date:` before quoted or narrative body (common in OEIS comments).
 */
export function splitOeisLeadingFromAttribution(text: string): { fromLine: string | null; body: string } {
  const norm = text.replace(/\r\n/g, "\n");
  const m = norm.match(/^\s*From\s+_([^_]+)_\s*,\s*(.+?)\s*:\s*/i);
  if (!m || m.index !== 0) {
    return { fromLine: null, body: norm };
  }
  const fromLine = norm.slice(0, m[0].length).trim();
  const body = norm.slice(m[0].length);
  return { fromLine: fromLine.length ? fromLine : null, body };
}

/** Renders `From _Name_, Jan 24 2010:` like other editorial credits (name + date + colon). */
export function formatOeisFromAttributionHtml(raw: string): string {
  const t = raw.trim();
  const m = t.match(/^From\s+_([^_]+)_\s*,\s*(.+?)\s*:\s*$/i);
  if (!m?.[1] || m[2] === undefined) {
    return escapeHtml(t);
  }
  const name = m[1].trim();
  const datePart = m[2].trim();
  return (
    `<span class="npp-oeis-author-sig npp-oeis-author-sig--from">` +
    `<span class="npp-oeis-author-verb">From </span>` +
    `<span class="npp-oeis-author-name">${escapeHtml(name)}</span>` +
    `<span class="npp-oeis-author-date">, ${escapeHtml(datePart)}</span>` +
    `<span class="npp-oeis-author-colon">:</span>` +
    `</span>`
  );
}

/**
 * Trailing OEIS credits: ` - _Author_, date` or `Corrected by _Author_, date` (end of block only).
 * A chunk that is **only** a byline (common as its own JSON string) has no leading whitespace before “Corrected”.
 */
export function splitOeisTrailingInlineAttribution(text: string): { body: string; attr: string | null } {
  const trimmedAll = text.trim();
  const peeledWhole = peelOptionalOuterBrackets(trimmedAll);
  const reByOnly = new RegExp(`^${OEIS_BYLINE_VERB}\\s+by\\s+_([^_]+)_\\s*,\\s*[^\\n]+$`, "i");
  const reUnderscoreOnly = /^_([^_]+)_\s*,\s*(.+)$/;
  const reDashOnly = /^-\s+_([^_]+)_\s*,\s*(.+)$/;
  if (reByOnly.test(peeledWhole)) {
    return { body: "", attr: peeledWhole };
  }
  if (reUnderscoreOnly.test(peeledWhole)) {
    return { body: "", attr: peeledWhole };
  }
  if (reDashOnly.test(peeledWhole)) {
    return { body: "", attr: peeledWhole };
  }

  const t = text.trimEnd();
  const reByBracketEnd = new RegExp(
    String.raw`\s*\[\s*` + OEIS_BYLINE_VERB + String.raw`\s+by\s+_([^_]+)_\s*,\s*([^\]\n]+)\s*\]$`,
    "i",
  );
  const reBy = new RegExp(String.raw`\s+` + OEIS_BYLINE_VERB + String.raw`\s+by\s+_([^_]+)_\s*,\s*([^\n]+)$`, "i");
  const reDash = /\s+-\s+_([^_]+)_\s*,\s*([^\n]+)$/;
  const m = t.match(reByBracketEnd) ?? t.match(reBy) ?? t.match(reDash);
  if (!m || m.index === undefined) return { body: text, attr: null };
  const body = t.slice(0, m.index).trimEnd();
  const attr = peelOptionalOuterBrackets(t.slice(m.index).trim());
  return { body, attr: attr.length ? attr : null };
}

/**
 * OEIS often stores short cross-reference sentences in `maple` / `mathematica` (e.g. “A076263 gives a
 * Mathematica program…”) instead of executable code. Skip those in code blocks.
 */
export function isLikelyOeisCasCrossrefProse(s: string): boolean {
  const t = s.trim();
  if (t.length < 30) return false;
  if (!/\bA\d{4,}\b/.test(t)) return false;
  const narrative =
    /\bgives\b/i.test(t) ||
    /\bsee also\b/i.test(t) ||
    /\bsee\s+the\s+sequence\b/i.test(t) ||
    /\bsee\s+A\d/i.test(t) ||
    /\bis\s+given\s+in\b/i.test(t);
  if (!narrative) return false;
  if (/:=/.test(t)) return false;
  if (/\[[^\]]+\]/.test(t)) return false;
  if (/\b(seq|add|mul|map|select|remove|subs|op)\s*\(/i.test(t)) return false;
  return true;
}

const OEIS_PURIFY: DOMPurify.Config = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: [
    "script",
    "iframe",
    "object",
    "embed",
    "form",
    "style",
    "link",
    "meta",
    "base",
    "textarea",
    "button",
    "input",
  ],
};

/** Remove script blocks so the parser never sees `<script>` (happy-dom + DOMPurify otherwise yield ""). */
function stripInlineScripts(html: string): string {
  return html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "");
}

/** Sanitize OEIS-provided HTML (e.g. `link` lines) and rewrite relative URLs / external link attributes. */
export function sanitizeOeisLinkHtml(html: string): string {
  const clean = DOMPurify.sanitize(stripInlineScripts(html), OEIS_PURIFY);
  return postprocessOeisSanitizedHtml(clean);
}

function postprocessOeisSanitizedHtml(sanitized: string): string {
  if (typeof document !== "undefined") {
    const wrap = document.createElement("div");
    wrap.innerHTML = sanitized;
    wrap.querySelectorAll("a[href]").forEach((el) => {
      const a = el as HTMLAnchorElement;
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      const href = a.getAttribute("href") || "";
      if (href.startsWith("/") && !href.startsWith("//")) {
        a.setAttribute("href", `https://oeis.org${href}`);
      }
    });
    wrap.querySelectorAll("img[src]").forEach((el) => {
      const img = el as HTMLImageElement;
      const src = img.getAttribute("src") || "";
      if (src.startsWith("/") && !src.startsWith("//")) {
        img.setAttribute("src", `https://oeis.org${src}`);
      }
    });
    return wrap.innerHTML;
  }
  return sanitized
    .replace(/href="\/(?!\/)/g, 'href="https://oeis.org/')
    .replace(/src="\/(?!\/)/g, 'src="https://oeis.org/');
}

/** Escape + linkify http(s) URLs, A-numbers, newlines; optional simple pipe table for whole block. */
export function oeisPlainTextToRichHtml(text: string): string {
  const trimmed = text.trimEnd();
  if (!trimmed) return "";
  const table = tryOeisTextTableToHtml(trimmed);
  if (table) return table;
  let s = escapeHtml(trimmed);
  s = s.replace(
    /(https?:\/\/[^\s<>"']+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  s = s.replace(/\b(A\d{4,})\b/g, (_m, id: string) => {
    const up = id.toUpperCase();
    return `<a class="npp-oeis-anum" href="https://oeis.org/${up}" target="_blank" rel="noopener noreferrer">${up}</a>`;
  });
  s = s.replace(/\n/g, "<br>");
  return s;
}

function tryOeisTextTableToHtml(raw: string): string | null {
  return tryPipeTableToHtml(raw) ?? tryAsciiBarTableToHtml(raw) ?? tryAsciiSpaceColumnsTableToHtml(raw);
}

function tryPipeTableToHtml(raw: string): string | null {
  const lines = raw.split(/\r?\n/).map((l) => l.trim());
  const nonempty = lines.filter((l) => l.length > 0);
  if (nonempty.length < 2) return null;
  if (!nonempty.every((l) => (l.match(/\|/g) || []).length >= 2)) return null;
  const rows = nonempty.map((line) =>
    line
      .split("|")
      .map((c) => c.trim())
      .filter((cell, i, arr) => !(cell === "" && (i === 0 || i === arr.length - 1))),
  );
  const ncols = Math.max(...rows.map((r) => r.length), 0);
  if (ncols < 2) return null;
  const pad = (r: string[]) => [...r, ...Array(Math.max(0, ncols - r.length)).fill("")].slice(0, ncols);
  const thead = `<thead><tr>${pad(rows[0]!)
    .map((c) => `<th>${escapeHtml(c)}</th>`)
    .join("")}</tr></thead>`;
  const tbodyRows = rows.slice(1).map((r) => {
    const cells = pad(r);
    return `<tr>${cells.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`;
  });
  return `<table class="npp-oeis-table npp-oeis-pipe-table">${thead}<tbody>${tbodyRows.join("")}</tbody></table>`;
}

/** OEIS ASCII table with `|` columns and optional dashed separator lines. */
function tryAsciiBarTableToHtml(raw: string): string | null {
  const lines = raw.split(/\r?\n/).map((l) => l.trimEnd());
  const rowLines = lines.filter((line) => (line.match(/\|/g) || []).length >= 2);
  if (rowLines.length < 2) return null;
  const rows = rowLines.map((line) =>
    line
      .split("|")
      .map((c) => c.trim())
      .filter((cell, i, arr) => !(cell === "" && (i === 0 || i === arr.length - 1))),
  );
  const usefulRows = rows.filter((r) => r.some((cell) => /[A-Za-z0-9]/.test(cell)));
  if (usefulRows.length < 2) return null;
  const ncols = Math.max(...usefulRows.map((r) => r.length), 0);
  if (ncols < 2) return null;
  const pad = (r: string[]) => [...r, ...Array(Math.max(0, ncols - r.length)).fill("")].slice(0, ncols);
  const header = pad(usefulRows[0]!);
  const bodyRows = usefulRows.slice(1).map((r) => pad(r));
  const thead = `<thead><tr>${header.map((c) => `<th>${escapeHtml(c)}</th>`).join("")}</tr></thead>`;
  const tbody = bodyRows.map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`).join("");
  return `<table class="npp-oeis-table npp-oeis-pipe-table npp-oeis-ascii-table">${thead}<tbody>${tbody}</tbody></table>`;
}

function splitAsciiSpaceColumns(line: string): string[] {
  return line.trim().split(/\s{2,}/).map((c) => c.trim()).filter((c) => c.length > 0);
}

/** OEIS ASCII table with whitespace-aligned columns and dashed separator row (no `|`). */
function tryAsciiSpaceColumnsTableToHtml(raw: string): string | null {
  const lines = raw.split(/\r?\n/).map((l) => l.trimEnd()).filter((l) => l.trim().length > 0);
  if (lines.length < 3) return null;
  const sepIdx = lines.findIndex((line) => /-{3,}\s{2,}-{3,}/.test(line.trim()));
  if (sepIdx <= 0 || sepIdx >= lines.length - 1) return null;
  const headerCols = splitAsciiSpaceColumns(lines[sepIdx - 1]!);
  if (headerCols.length < 2) return null;
  const ncols = headerCols.length;
  const bodyRows: string[][] = [];
  for (let i = sepIdx + 1; i < lines.length; i++) {
    const cols = splitAsciiSpaceColumns(lines[i]!);
    if (cols.length !== ncols) continue;
    bodyRows.push(cols);
  }
  if (!bodyRows.length) return null;
  const thead = `<thead><tr>${headerCols.map((c) => `<th>${escapeHtml(c)}</th>`).join("")}</tr></thead>`;
  const tbody = bodyRows.map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`).join("");
  return `<table class="npp-oeis-table npp-oeis-pipe-table npp-oeis-ascii-table npp-oeis-ascii-space-table">${thead}<tbody>${tbody}</tbody></table>`;
}

function isMarkdownHr(chunk: string): boolean {
  const t = chunk.trim().replace(/\s+/g, "");
  return /^(?:-{3,}|\*{3,}|_{3,})$/.test(t);
}

function tryFencedCodeBlock(chunk: string): string | null {
  const lines = chunk.split("\n");
  if (lines.length < 2) return null;
  const open = lines[0]!.trim();
  if (!open.startsWith("```")) return null;
  const closeIdx = lines.findIndex((line, idx) => idx > 0 && line.trim() === "```");
  if (closeIdx < 1) return null;
  const inner = lines.slice(1, closeIdx).join("\n");
  return `<pre class="npp-oeis-md-pre" tabindex="0"><code>${escapeHtml(inner)}</code></pre>`;
}

function oeisMdEscapedDecorators(escaped: string): string {
  let t = applyOeisUnderscoreItalicsEscaped(escaped);
  t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(
    /(https?:\/\/[^\s<>"']+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  t = t.replace(/\b(A\d{4,})\b/g, (_m, id: string) => {
    const up = id.toUpperCase();
    return `<a class="npp-oeis-anum" href="https://oeis.org/${up}" target="_blank" rel="noopener noreferrer">${up}</a>`;
  });
  return t;
}

const INLINE_FORMULA_RE =
  /([A-Za-z]\([^\n)]{0,120}\)\s*(?:==|!=|<=|>=|=|<|>)\s*[^,.;\n]+|\b(?:gcd|lcm|sqrt|binomial)\s*\([^\n)]{1,120}\)|\bO\([^,\n]{1,140}\)+|\b[A-Za-z0-9_]+\s*(?:==|!=|<=|>=)\s*[A-Za-z0-9_()+\-/*^]+|\b[A-Z]_[A-Za-z0-9]+\b(?:\s*(?:X|=)\s*\b[A-Z]_[A-Za-z0-9]+\b)+|\b[A-Z]_[A-Za-z0-9]+\b)/g;

/** Render formula-like inline chunks as explicit text spans (comments/examples/formula prose). */
export function renderInlineFormulaTextHtml(raw: string): string {
  const input = replaceGreekNamesUnicode(raw);
  const formatSubscripts = (src: string): string => {
    const normalized = normalizeOeisTrailingParenImbalance(normalizeOeisCaretParenExponents(src));
    const escaped = escapeHtml(normalized);
    return escaped.replace(
      /\b([A-Za-z0-9)\]])_([A-Za-z0-9+\-*/()]+)\b/g,
      (_m, base: string, sub: string) => `${base}<sub>${sub}</sub>`,
    );
  };
  const out: string[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE_FORMULA_RE.lastIndex = 0;
  while ((m = INLINE_FORMULA_RE.exec(input)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (start > last) out.push(oeisMdEscapedDecorators(escapeHtml(input.slice(last, start))));
    out.push(`<span class="npp-oeis-inline-formula-text">${formatSubscripts(m[0])}</span>`);
    last = end;
  }
  if (last < input.length) out.push(oeisMdEscapedDecorators(escapeHtml(input.slice(last))));
  return out.join("");
}

/** Inline rich text for Example / Comments: backtick code, **bold**, _OEIS italics_, URLs, A-numbers. */
export function oeisProseInlineRichHtml(raw: string): string {
  const s = raw;
  const parts: string[] = [];
  let i = 0;
  while (i < s.length) {
    const b = s.indexOf("`", i);
    if (b === -1) {
      parts.push(renderInlineFormulaTextHtml(s.slice(i)));
      break;
    }
    if (b > i) {
      parts.push(renderInlineFormulaTextHtml(s.slice(i, b)));
    }
    const b2 = s.indexOf("`", b + 1);
    if (b2 === -1) {
      parts.push(escapeHtml(s.slice(b)));
      break;
    }
    parts.push(`<code class="npp-oeis-md-code">${escapeHtml(s.slice(b + 1, b2))}</code>`);
    i = b2 + 1;
  }
  return parts.join("");
}

function tryMdHeadingLine(line: string): string | null {
  const m = line.trim().match(/^(#{1,6})\s+(.+)$/);
  if (!m?.[1] || m[2] === undefined) return null;
  const n = m[1].length;
  const level = n === 1 ? 4 : n === 2 ? 5 : 6;
  return `<h${level} class="npp-oeis-md-h">${oeisProseInlineRichHtml(m[2])}</h${level}>`;
}

function unwrapBracketsOnlyIfFromLead(s: string): string {
  const te = s.trimEnd();
  const openIdx = te.indexOf("[");
  if (openIdx < 0 || !te.endsWith("]")) {
    return s;
  }
  const inner = te.slice(openIdx + 1, -1).trim();
  if (!/^\s*From\s+/i.test(inner)) {
    return s;
  }
  return te.slice(0, openIdx) + inner + s.slice(te.length);
}

function renderOeisMdStartEndMarkersLine(line: string): string {
  const t = line.trim();
  if (/^\(start\)$/i.test(t)) {
    return `<span class="npp-oeis-md-marker npp-oeis-md-marker-start">(Start)</span>`;
  }
  if (/^\(end\)$/i.test(t)) {
    return `<span class="npp-oeis-md-marker npp-oeis-md-marker-end">(End)</span>`;
  }
  const endTail = t.match(/^(.*?)(?:\.)?\s*\(end\)\s*\.?\s*$/i);
  if (endTail?.[1] !== undefined) {
    const core = endTail[1].trimEnd();
    if (!core) {
      return `<span class="npp-oeis-md-marker npp-oeis-md-marker-end">(End)</span>`;
    }
    return (
      `${oeisProseInlineRichHtml(core)}` +
      ` <span class="npp-oeis-md-marker npp-oeis-md-marker-end">(End)</span>`
    );
  }
  return oeisProseInlineRichHtml(line);
}

function formatOeisMdParagraphBody(body: string): string {
  const norm = body.replace(/\r\n/g, "\n");
  const work = unwrapBracketsOnlyIfFromLead(norm);
  const { fromLine, body: afterFrom } = splitOeisLeadingFromAttribution(work);
  const { body: core, attr } = splitOeisTrailingInlineAttribution(afterFrom);
  const mainHtml =
    !/\S/.test(core)
      ? ""
      : core
          .split("\n")
          .map((ln) => renderOeisMdStartEndMarkersLine(ln))
          .join("<br>\n");
  const sigHtml = attr ? formatOeisAuthorLineHtml(attr) : "";
  const fromHtml = fromLine ? formatOeisFromAttributionHtml(fromLine) : "";
  const parts: string[] = [];
  if (sigHtml) {
    parts.push(`<span class="npp-oeis-md-p-attr">${sigHtml}</span>`);
  }
  if (fromHtml) {
    parts.push(`<span class="npp-oeis-md-p-from">${fromHtml}</span>`);
  }
  if (mainHtml) {
    parts.push(`<span class="npp-oeis-md-p-main">${mainHtml}</span>`);
  }
  if (parts.length) {
    return parts.join("");
  }
  return mainHtml;
}

export type OeisMarkdownLikeOptions = {
  /** When true (Example section), do not trim outer text or blank-line chunks so spaces/indentation survive. */
  preserveExampleWhitespace?: boolean;
};

/** Blank-line paragraphs, lists, blockquotes, fenced ``` ```, pipe tables, `---` rules (Example / Comments). */
export function oeisMarkdownLikeDocumentHtml(text: string, opts?: OeisMarkdownLikeOptions): string {
  const preserve = !!opts?.preserveExampleWhitespace;
  const raw = text.replace(/\r\n/g, "\n");
  const norm = preserve ? raw : raw.trim();
  if (!/\S/.test(norm)) return "";
  const rawChunks = norm.split(/\n\n+/);
  const chunks = preserve
    ? rawChunks.filter((c) => c.length > 0)
    : rawChunks.map((c) => c.trim()).filter((c) => c.length > 0);
  const out: string[] = [];
  for (const chunk of chunks) {
    if (isMarkdownHr(chunk)) {
      out.push('<hr class="npp-oeis-md-hr" />');
      continue;
    }
    const table = tryOeisTextTableToHtml(chunk);
    if (table) {
      out.push(`<div class="npp-oeis-md-tablewrap">${table}</div>`);
      continue;
    }
    const fence = tryFencedCodeBlock(chunk);
    if (fence) {
      out.push(fence);
      continue;
    }
    const rawLines = chunk.split("\n");
    const nonempty = rawLines.map((l) => l.trim()).filter((l) => l.length > 0);
    if (!nonempty.length) continue;
    if (nonempty.length === 1) {
      const h = tryMdHeadingLine(nonempty[0]!);
      if (h) {
        out.push(h);
        continue;
      }
    }
    if (nonempty.every((l) => /^>\s?/.test(l))) {
      const inner = nonempty
        .map((l) => oeisProseInlineRichHtml(l.replace(/^>\s?/, "")))
        .join("<br>\n");
      out.push(`<blockquote class="npp-oeis-md-bq">${inner}</blockquote>`);
      continue;
    }
    const listLines = rawLines.filter((l) => l.trim().length > 0);
    if (listLines.length && listLines.every((l) => /^\s*[-*]\s+/.test(l))) {
      const items = listLines.map((l) => l.replace(/^\s*[-*]\s+/, "").trimEnd());
      out.push(
        `<ul class="npp-oeis-md-ul">${items.map((body) => `<li>${formatOeisMdParagraphBody(body)}</li>`).join("")}</ul>`,
      );
      continue;
    }
    if (listLines.length && listLines.every((l) => /^\s*\d+\.\s+/.test(l))) {
      const items = listLines.map((l) => l.replace(/^\s*\d+\.\s+/, "").trimEnd());
      out.push(
        `<ol class="npp-oeis-md-ol">${items.map((body) => `<li>${formatOeisMdParagraphBody(body)}</li>`).join("")}</ol>`,
      );
      continue;
    }
    out.push(`<p class="npp-oeis-md-p">${formatOeisMdParagraphBody(chunk)}</p>`);
  }
  return out.join("");
}

function sectionOeisMarkdownProse(title: string, items: readonly string[], sectionClass: string): string {
  const nonempty = items.map((s) => String(s)).filter((s) => s.trim().length > 0);
  if (!nonempty.length) return "";
  const merged = nonempty.join("\n\n");
  const preserveExample = sectionClass === "npp-oeis-section--example";
  const inner = oeisMarkdownLikeDocumentHtml(merged, preserveExample ? { preserveExampleWhitespace: true } : undefined);
  if (!inner) return "";
  const mdClass = preserveExample ? "npp-oeis-md npp-oeis-md--preserve-space" : "npp-oeis-md";
  return (
    `<section class="npp-oeis-section npp-oeis-section--md ${sectionClass}">` +
    `<h3>${escapeHtml(title)}</h3>` +
    `<div class="${mdClass}">${inner}</div>` +
    `</section>`
  );
}

/**
 * OEIS often writes grouped exponents as `a^(n+1)` or `((1+sqrt(5))^n`; TeX expects `a^{n+1}`.
 * Rewrites each `^(` … balanced `)` … into `^{(` … `)}` to preserve explicit grouping.
 */
export function normalizeOeisCaretParenExponents(s: string): string {
  const rewriteOnce = (input: string): string => {
    let result = "";
    let i = 0;
    while (i < input.length) {
      if (input[i] === "^" && i + 1 < input.length && input[i + 1] === "(") {
        let depth = 1;
        let j = i + 2;
        while (j < input.length && depth > 0) {
          if (input[j] === "(") depth++;
          else if (input[j] === ")") depth--;
          j++;
        }
        if (depth === 0) {
          const inner = input.slice(i + 2, j - 1);
          result += "^{(" + inner + ")}";
          i = j;
          continue;
        }
      }
      result += input[i];
      i++;
    }
    return result;
  };

  let prev = s;
  for (let k = 0; k < 8; k++) {
    const next = rewriteOnce(prev);
    if (next === prev) return next;
    prev = next;
  }
  return prev;
}

/**
 * OEIS sometimes uses sequence calls directly after `^` (e.g. `(-1)^A156253(n)`), while TeX expects
 * grouped exponents. Rewrites `^Axxxxxx(...)` to `^{Axxxxxx(...)}` with balanced-paren parsing.
 */
export function normalizeOeisCaretAnumberCallExponents(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "^" && i + 1 < s.length) {
      const rest = s.slice(i + 1);
      const m = rest.match(/^A\d{4,}\(/i);
      if (m) {
        let j = i + 1 + m[0].length;
        let depth = 1;
        while (j < s.length && depth > 0) {
          if (s[j] === "(") depth++;
          else if (s[j] === ")") depth--;
          j++;
        }
        if (depth === 0) {
          const call = s.slice(i + 1, j);
          out += `^{${call}}`;
          i = j;
          continue;
        }
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/**
 * Grouped exponents on identifier calls: `2^gcd(n,k)` → `2^{gcd(n,k)}` (balanced parens).
 */
export function normalizeOeisCaretIdentifierCallExponents(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "^" && i + 1 < s.length && s[i + 1] !== "{" && s[i + 1] !== "(") {
      const rest = s.slice(i + 1);
      const m = rest.match(/^([A-Za-z]+)\(/);
      if (m) {
        let j = i + 1 + m[0].length;
        let depth = 1;
        while (j < s.length && depth > 0) {
          if (s[j] === "(") depth++;
          else if (s[j] === ")") depth--;
          j++;
        }
        if (depth === 0) {
          const call = s.slice(i + 1, j);
          out += `^{${call}}`;
          i = j;
          continue;
        }
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/** Drop unmatched ')' anywhere so malformed fragments like `...O(e^(8/3))).` can still render. */
export function normalizeOeisTrailingParenImbalance(s: string): string {
  let depth = 0;
  let out = "";
  for (const ch of s) {
    if (ch === "(") {
      depth++;
      out += ch;
      continue;
    }
    if (ch === ")") {
      if (depth > 0) {
        depth--;
        out += ch;
      }
      continue;
    }
    out += ch;
  }
  return out;
}

/** Strip a single pair of outer `$...$` so pasted TeX survives KaTeX (no math delimiters in input). */
function stripOptionalOuterTexDollars(s: string): string {
  const t = s.trim();
  if (t.length >= 2 && t.startsWith("$") && t.endsWith("$")) {
    const inner = t.slice(1, -1).trim();
    if (inner.length > 0 && !inner.includes("$")) {
      return inner;
    }
  }
  return t;
}

/**
 * OEIS range sums/products: `Sum_{i=1..n-1}` → `\sum_{i=1}^{n-1}` (and Product analog).
 * Applied before generic `Sum_{` → `\sum_{` rewrite.
 */
function normalizeOeisSumProductDotRangeBraces(s: string): string {
  const re = /\b(Sum|Product)_\{/gi;
  let out = "";
  let pos = 0;
  while (pos < s.length) {
    re.lastIndex = pos;
    const m = re.exec(s);
    if (!m || m.index === undefined || m[1] === undefined) {
      out += s.slice(pos);
      break;
    }
    const cmd = m[1];
    const abs = m.index;
    const openBrace = abs + m[0].length - 1;
    let depth = 0;
    let j = openBrace;
    for (; j < s.length; j++) {
      if (s[j] === "{") depth++;
      else if (s[j] === "}") {
        depth--;
        if (depth === 0) break;
      }
    }
    if (depth !== 0 || j >= s.length) {
      out += s.slice(pos, abs + 1);
      pos = abs + 1;
      continue;
    }
    const inner = s.slice(openBrace + 1, j).trim();
    const rangeM = inner.match(/^([A-Za-z])\s*=\s*(.+)\.\.(.+)$/);
    if (!rangeM || rangeM[1] === undefined || rangeM[2] === undefined || rangeM[3] === undefined) {
      out += s.slice(pos, abs + 1);
      pos = abs + 1;
      continue;
    }
    const v = rangeM[1].trim();
    const lo = rangeM[2].trim();
    const hi = rangeM[3].trim();
    const texCmd = cmd.toLowerCase() === "product" ? "\\prod" : "\\sum";
    out += s.slice(pos, abs) + `${texCmd}_{${v}=${lo}}^{${hi}}`;
    pos = j + 1;
  }
  return out;
}

const GREEK_LOWER_TEX: Array<[string, string]> = [
  ["alpha", "\\alpha "],
  ["beta", "\\beta "],
  ["gamma", "\\gamma "],
  ["delta", "\\delta "],
  ["eta", "\\eta "],
  ["theta", "\\theta "],
  ["zeta", "\\zeta "],
  ["lambda", "\\lambda "],
  ["sigma", "\\sigma "],
  ["nu", "\\nu "],
  ["mu", "\\mu "],
  ["tau", "\\tau "],
  ["phi", "\\phi "],
  ["omega", "\\omega "],
  ["pi", "\\pi "],
  /* bigomega must come before omega so the \b boundary check works correctly */
  ["bigomega", "\\Omega "],
];
const GREEK_UPPER_TEX: Array<[string, string]> = [
  ["Alpha", "Α"],
  ["Beta", "Β"],
  ["Gamma", "\\Gamma "],
  ["Delta", "\\Delta "],
  ["Eta", "Η"],
  ["Zeta", "Ζ"],
  ["Lambda", "\\Lambda "],
  ["Sigma", "\\Sigma "],
  ["Nu", "Ν"],
  ["Mu", "Μ"],
  ["Tau", "Τ"],
  ["Phi", "\\Phi "],
  ["Omega", "\\Omega "],
  ["Pi", "\\pi "],
];
const GREEK_LOWER_UNI: Array<[string, string]> = [
  ["alpha", "α"],
  ["beta", "β"],
  ["gamma", "γ"],
  ["delta", "δ"],
  ["eta", "η"],
  ["theta", "θ"],
  ["zeta", "ζ"],
  ["lambda", "λ"],
  ["sigma", "σ"],
  ["nu", "ν"],
  ["mu", "μ"],
  ["tau", "τ"],
  ["phi", "φ"],
  ["omega", "ω"],
  ["pi", "π"],
  ["bigomega", "Ω"],
];
const GREEK_UPPER_UNI: Array<[string, string]> = [
  ["Alpha", "Α"],
  ["Beta", "Β"],
  ["Gamma", "Γ"],
  ["Delta", "Δ"],
  ["Eta", "Η"],
  ["Zeta", "Ζ"],
  ["Lambda", "Λ"],
  ["Sigma", "Σ"],
  ["Nu", "Ν"],
  ["Mu", "Μ"],
  ["Tau", "Τ"],
  ["Phi", "Φ"],
  ["Omega", "Ω"],
  ["Pi", "π"],
];

/** Replace Greek name words with LaTeX commands (for the KaTeX rendering path). */
function replaceOeisNamedGreekSymbols(s: string): string {
  let out = s;
  for (const [name, repl] of GREEK_LOWER_TEX) {
    out = out.replace(new RegExp(`\\b${name}\\b`, "g"), repl);
  }
  for (const [name, repl] of GREEK_UPPER_TEX) {
    out = out.replace(new RegExp(`\\b${name}\\b`, "g"), repl);
  }
  return out;
}

/** Replace Greek name words with Unicode characters (for prose / fallback HTML rendering paths). */
function replaceGreekNamesUnicode(s: string): string {
  let out = s;
  for (const [name, repl] of GREEK_LOWER_UNI) {
    out = out.replace(new RegExp(`\\b${name}\\b`, "g"), repl);
  }
  for (const [name, repl] of GREEK_UPPER_UNI) {
    out = out.replace(new RegExp(`\\b${name}\\b`, "g"), repl);
  }
  /* OEIS compact "forn" (for + n without space) → "for n". */
  out = out.replace(/\bforn\b/g, "for n");
  return out;
}

/** Map common OEIS ASCII notation toward LaTeX KaTeX accepts (best-effort). */
export function normalizeOeisMathNotationForKatex(s: string): string {
  let t = stripOptionalOuterTexDollars(s);
  /* OEIS prose divisibility: "d divides n" → "d|n" */
  t = t.replace(/\b([A-Za-z][A-Za-z0-9_]*)\s+divides\s+([A-Za-z][A-Za-z0-9_]*)\b/gi, "$1|$2");
  /* e.g. "distinct primesp dividing n", "distinct primes p dividing n" */
  t = t.replace(
    /\bdistinct\s+(prime|primes)(?:\s+p|p)\s+dividing\s+([A-Za-z][A-Za-z0-9_]*)\b/gi,
    (_m, kind: string, rhs: string) => {
      const label = kind.toLowerCase() === "prime" ? "distinct prime" : "distinct primes";
      return `\\text{${label}}\\, p \\mid ${rhs}`;
    },
  );
  t = normalizeOeisCaretParenExponents(t);
  t = normalizeOeisCaretAnumberCallExponents(t);
  t = normalizeOeisCaretIdentifierCallExponents(t);
  t = normalizeOeisTrailingParenImbalance(t);
  t = normalizeOeisSumProductDotRangeBraces(t);
  t = replaceOeisNamedGreekSymbols(t);
  /* OEIS ASCII multiplication `*` → TeX `\cdot` (skip `**`; trim surrounding spaces). */
  t = t.replace(/(?<!\*)\s*\*(?!\*)\s*/g, " \\cdot ");
  t = t.replace(/\bSum_\{/g, "\\sum_{");
  t = t.replace(/\bProduct_\{/g, "\\prod_{");
  t = t.replace(/\blim_\{/g, "\\lim_{");
  t = t.replace(/\\lim_\{([^{}]*?)[−-]>\s*oo\}/gi, "\\lim_{$1\\to \\infty}");
  /* Asymptotic "arrow to infinity": ASCII/unicode minus, optional spaces (e.g. "- > oo", "−>oo") → \to \infty */
  t = t.replace(/(?:[-−\u2212\u2013\u2014])\s*>\s*oo\b/gi, "\\to \\infty");
  t = t.replace(/binomial\s*\(\s*([^,()]+)\s*,\s*([^)]+)\)/gi, "\\binom{$1}{$2}");
  t = t.replace(/\bsqrt\s*\(\s*([^)\s]+)\s*\)/gi, "\\sqrt{$1}");
  t = t.replace(/\bfloor\s*\(\s*([^)]+)\)/gi, "\\left\\lfloor $1 \\right\\rfloor");
  t = t.replace(/\bceiling\s*\(\s*([^)]+)\)/gi, "\\left\\lceil $1 \\right\\rceil");
  t = t.replace(/\bround\s*\(/gi, "\\operatorname{round}(");
  t = t.replace(/(?<!\\)\bcos\b/g, "\\cos ");
  t = t.replace(/(?<!\\)\bsin\b/g, "\\sin ");
  t = t.replace(/(?<!\\)\bexp\b/g, "\\exp ");
  /* English prose words in formula context should render as upright text, not math italic.
   * "for" must run before the forn expansion so it doesn't accidentally match inside \text{for}\,n. */
  t = t.replace(/(?<!\\)\bfor\b/g, "\\text{for}");
  t = t.replace(/(?<!\\)\belse\b/g, "\\text{else}");
  /* OEIS compact "forn" (for + n concatenated without space) → "for n" as text+variable. */
  t = t.replace(/\bforn\b/g, "\\text{for}\\,n");
  t = t.replace(/(?<!\\)\binfty\b/gi, "\\infty ");
  t = t.replace(/>=/g, "\\geq ");
  t = t.replace(/<=/g, "\\leq ");
  t = t.replace(/!=/g, "\\neq ");
  return t;
}

/**
 * Each internal newline in a formula chunk becomes its own row (KaTeX or rich fallback).
 */
function renderOeisFormulaMathSegments(mathSrc: string): string {
  const raw = mathSrc.trim();
  if (!raw) return "";
  const segments = raw.split(/\n/).map((x) => x.trim()).filter((x) => x.length > 0);
  const rows: string[] = [];
  for (const seg of segments) {
    const normalized = normalizeOeisMathNotationForKatex(seg);
    let out = katex.renderToString(normalized, {
      displayMode: true,
      throwOnError: false,
      strict: "ignore",
    });
    if (out.includes("katex-error")) {
      out = katex.renderToString(normalized, {
        displayMode: false,
        throwOnError: false,
        strict: "ignore",
      });
    }
    if (out.includes("katex-error")) {
      rows.push(
        `<div class="npp-oeis-formula-mathrow npp-oeis-formula-mathrow--text npp-oeis-math-target">` +
          `<span class="npp-oeis-formula-fallback">${oeisFormulaFallbackRichHtml(seg)}</span>` +
          `</div>`,
      );
    } else {
      rows.push(`<div class="npp-oeis-formula-mathrow">${out}</div>`);
    }
  }
  return rows.join("");
}

/** OEIS formula lines often end with ` - _Author_, date`. */
export function stripOeisFormulaAttribution(text: string): { core: string; author: string | null; tail: string | null } {
  const t = text.trimEnd();
  const m = t.match(/\s+-\s+_([^_]+)_\s*,\s*([^\n]+)$/);
  if (!m || m.index === undefined) return { core: t, author: null, tail: null };
  const core = t.slice(0, m.index).trimEnd();
  return { core, author: m[1] ?? null, tail: m[2] ?? null };
}

/** `_word_` italics (OEIS convention) on already-escaped HTML. */
function applyOeisUnderscoreItalicsEscaped(escaped: string): string {
  return escaped.replace(/_([A-Za-z][A-Za-z0-9]*)_/g, "<em>$1</em>");
}

/**
 * Breaks long formula prose into multiple physical lines: wrap at spaces near `maxCol`,
 * and hard-split tokens longer than `maxCol` (so text does not stay on one overflowing line).
 */
export function softWrapOeisFormulaPlainText(text: string, maxCol = 88): string {
  const norm = text.replace(/\r\n/g, "\n");
  const out: string[] = [];
  for (const para of norm.split("\n")) {
    if (para.length <= maxCol) {
      out.push(para);
      continue;
    }
    let line = "";
    const parts = para.split(/(\s+)/);
    for (const part of parts) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        if (line.length + part.length <= maxCol) {
          line += part;
        } else {
          if (line.trim()) out.push(line.trimEnd());
          line = part.replace(/^\s+/, "");
        }
        continue;
      }
      if (part.length > maxCol) {
        if (line.trim()) {
          out.push(line.trimEnd());
          line = "";
        }
        for (let i = 0; i < part.length; i += maxCol) {
          out.push(part.slice(i, i + maxCol));
        }
        continue;
      }
      if (line.length + part.length <= maxCol) {
        line += part;
      } else {
        if (line.trim()) out.push(line.trimEnd());
        line = part;
      }
    }
    if (line.length) out.push(line.trimEnd());
  }
  return out.join("\n");
}

function oeisFormulaFallbackRichHtml(raw: string): string {
  const wrapped = replaceGreekNamesUnicode(softWrapOeisFormulaPlainText(raw.trim(), 88));
  let s = escapeHtml(wrapped);
  s = applyOeisUnderscoreItalicsEscaped(s);
  s = s.replace(
    /(https?:\/\/[^\s<>"']+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  s = s.replace(/\b(A\d{4,})\b/g, (_m, id: string) => {
    const up = id.toUpperCase();
    return `<a class="npp-oeis-anum" href="https://oeis.org/${up}" target="_blank" rel="noopener noreferrer">${up}</a>`;
  });
  s = s.replace(/\n/g, "<br>");
  return s;
}

/**
 * Split `Label: value` when the head is a short OEIS tag (G.f., Theorem 1, …), not `http:` or `a(n)=…`.
 */
function splitOeisFormulaLabelLine(line: string): { label: string | null; body: string } {
  const t = line.trim();
  const ci = t.indexOf(":");
  if (ci < 2 || ci > 46) return { label: null, body: t };
  const head = t.slice(0, ci).trim();
  const body = t.slice(ci + 1).trim();
  if (!body) return { label: null, body: t };
  if (/^[a-z][a-z0-9.]*:\/\//i.test(head)) return { label: null, body: t };
  if (head.length > 46) return { label: null, body: t };
  if (/=/.test(head)) return { label: null, body: t };
  return { label: head, body };
}

/**
 * Heuristic guard: many OEIS `formula` lines are prose ("For p,q,r primes:").
 * Render those as rich text instead of forcing KaTeX.
 */
function isLikelyOeisFormulaMathSource(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  const proseCue =
    /\bis one of the following\b/i.test(t) ||
    /\botherwise\b/i.test(t) ||
    /\bfor\s+[^:]{0,80}\bprimes?\b/i.test(t) ||
    /\btable\s+from\b/i.test(t) ||
    /* "In particular" / "Inparticular" — always prose, even if the line also has "=" */
    /\bin\s*particular\b/i.test(t) ||
    /^\(?start\)?$/i.test(t) ||
    /^\(?end\)?$/i.test(t);
  const strongMath = /[=^<>]|\\[A-Za-z]+|_\{?|\b(a\(|f\(|g\(|h\()/.test(t);
  const mathWords = /\b(gcd|lcm|sqrt|binomial|sum|prod|mod|phi|pi|infty)\b/i.test(t);
  const numericOps = /\d\s*[-+*/]\s*\d|\d+\s*[<>]=?\s*[A-Za-z0-9(]/.test(t);
  const languageWords = /\b(for|from|with|which|where|when|otherwise|following|table|primes?|inparticular|particular)\b/i.test(t);
  const wordCount = (t.match(/[A-Za-z]{2,}/g) || []).length;
  if (proseCue && !/\\[A-Za-z]+/.test(t)) return false;
  if (!(strongMath || mathWords || numericOps)) return false;
  if (!strongMath && languageWords && wordCount >= 4) return false;
  return true;
}

function hasFormulaMathSignal(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  return /[=^<>]|\\[A-Za-z]+|_\{?|\b(a\(|f\(|g\(|h\()/.test(t) || /\b(gcd|lcm|sqrt|binomial|sum|prod|mod|phi|pi|infty)\b/i.test(t);
}

function hasFormulaCueWords(raw: string): boolean {
  return /\b(coefficient of|in polynomial|fibonacci numbers?|lucas numbers?|row sums? of triangle|trace of the|matrix|continued fraction|multiplicative with|see\s+.+also\s+A\d{4,}|is one of the following|is the sequence|is written as|is convergent|is prime|is odd|otherwise|then|for\s+all\s+integers?|for\s+every|for\s+[^:]{0,80}\bprimes?|table from|iff|if|where|when|inparticular|particular)\b/i.test(raw);
}

function isLikelyOeisFormulaSentenceText(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  const wordCount = (t.match(/[A-Za-z]{2,}/g) || []).length;
  if (wordCount < 6) return false;
  const hasSentenceVerb = /\b(define|defines|defined|doesn(?:'|’)t|does not|is|are|was|were|be|being|have|has|had)\b/i.test(t);
  const hasSequenceNarrative = /\bis\s+the\s+sequence\b/i.test(t) || /\bdefine(?:s|d)?\s+.*\bsequence\b/i.test(t);
  const hasConnectorWords = /\b(and|or|not|just|only|this|these|that|it|its|their|there)\b/i.test(t);
  const sentenceLikePunctuation = /[.;:!?]\s*$/.test(t) || /,\s+(and|or|but)\b/i.test(t);
  const startsLikeSentence = /^(this|these|that|there|it)\b/i.test(t);
  return (hasSentenceVerb && hasConnectorWords) || (startsLikeSentence && hasSentenceVerb) || hasSequenceNarrative || sentenceLikePunctuation;
}

function isLikelyOeisPlainDashByline(raw: string): boolean {
  const t = raw.trim();
  if (!/^(?:-|–|—|−)\s*[^,]+,\s*[A-Za-z]{3}\s+\d{1,2}\s+\d{4}\.?$/.test(t)) return false;
  const beforeComma = t.replace(/^(?:-|–|—|−)\s*/, "").replace(/\s*,[\s\S]*$/, "");
  if (/[=^<>]|\b(a\(|f\(|g\(|h\()/.test(beforeComma)) return false;
  return /[A-Za-z]/.test(t);
}

function hasFormulaQuantifierCue(raw: string): boolean {
  return /\bfor\s+all\s+integers?\b/i.test(raw) || /\bfor\s+every\b/i.test(raw);
}

function hasFormulaIffCue(raw: string): boolean {
  return /\biff\b/i.test(raw);
}

function isLikelyInlineVariableMathToken(raw: string): boolean {
  const t = raw.trim().replace(/[.,;:!?]+$/, "").trim();
  if (!t) return false;
  return /^[A-Za-z](?:_[A-Za-z0-9]+)?(?:\([^()\n]{1,60}\))?$/.test(t);
}

function renderOeisInlineMathOrText(seg: string): string {
  const t = seg.trim();
  if (!t) return "";
  if (!hasFormulaMathSignal(t)) {
    if (isLikelyInlineVariableMathToken(t)) {
      const normalized = normalizeOeisMathNotationForKatex(t.replace(/[.,;:!?]+$/, "").trim());
      const punct = t.match(/[.,;:!?]+$/)?.[0] ?? "";
      const out = katex.renderToString(normalized, {
        displayMode: false,
        throwOnError: false,
        strict: "ignore",
      });
      if (!out.includes("katex-error")) {
        return `${out}${escapeHtml(punct)}`;
      }
    }
    return `<span class="npp-oeis-formula-fallback">${oeisFormulaFallbackRichHtml(t)}</span>`;
  }
  const normalized = normalizeOeisMathNotationForKatex(t);
  let out = katex.renderToString(normalized, {
    displayMode: false,
    throwOnError: false,
    strict: "ignore",
  });
  if (out.includes("katex-error")) {
    return `<span class="npp-oeis-formula-fallback">${oeisFormulaFallbackRichHtml(t)}</span>`;
  }
  return out;
}

function renderOeisMixedFormulaInner(line: string): string | null {
  const t = line.trim();
  if (!t) return null;
  const cueRe =
    /\b(coefficient of|in polynomial|fibonacci numbers?|lucas numbers?|row sums? of triangle|trace of the|matrix|continued fraction|multiplicative with|see\s+.+also\s+A\d{4,}|is one of the following|is the sequence|is written as|is convergent|is prime|is odd|otherwise|then|for\s+all\s+integers?|for\s+every|for\s+[^:]{0,80}\bprimes?|table from|iff|if|where|when)\b/gi;
  const cueSingle =
    /^(coefficient of|in polynomial|fibonacci numbers?|lucas numbers?|row sums? of triangle|trace of the|matrix|continued fraction|multiplicative with|see\s+.+also\s+A\d{4,}|is one of the following|is the sequence|is written as|is convergent|is prime|is odd|otherwise|then|for\s+all\s+integers?|for\s+every|for\s+[^:]{0,80}\bprimes?|table from|iff|if|where|when)$/i;
  const parts: string[] = [];
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = cueRe.exec(t)) !== null) {
    const start = m.index;
    const end = m.index + m[0].length;
    if (start > idx) parts.push(t.slice(idx, start));
    parts.push(t.slice(start, end));
    idx = end;
  }
  if (idx < t.length) parts.push(t.slice(idx));
  if (parts.length <= 1) return null;
  const inner = parts
    .map((seg) => {
      const s = seg.trim();
      if (!s) return "";
      if (cueSingle.test(s) || /^[,:;().-]+$/.test(s)) {
        return `<span class="npp-oeis-formula-fallback">${oeisFormulaFallbackRichHtml(s)}</span>`;
      }
      return renderOeisInlineMathOrText(s);
    })
    .filter((h) => h.length > 0)
    .join(`<span class="npp-oeis-formula-fallback"> </span>`);
  return inner || null;
}

function renderOeisMixedFormulaLine(line: string): string | null {
  const inner = renderOeisMixedFormulaInner(line);
  if (!inner) return null;
  return `<div class="npp-oeis-formula-line npp-oeis-formula-line--text npp-oeis-math-target">${inner}</div>`;
}

function renderOeisFormulaPhysicalLine(line: string): string {
  const norm = line.replace(/\r\n/g, "\n");
  const lines = norm.split("\n");
  if (lines.length >= 2) {
    const first = lines[0]!.trim();
    if (/^\(start\)$/i.test(first)) {
      const rest = lines.slice(1).join("\n");
      const lastLine = lines[lines.length - 1]!.trim();
      if (/^\(end\)$/i.test(lastLine)) {
        const middle = lines.slice(1, -1).join("\n").trim();
        return (
          renderOeisFormulaPhysicalLine("(Start)") +
          (middle ? renderOeisFormulaPhysicalLine(middle) : "") +
          renderOeisFormulaPhysicalLine("(End)")
        );
      }
      const endTailRest = rest.match(/^([\s\S]*?)(?:\.)?\s*\(end\)\s*\.?\s*$/i);
      if (endTailRest?.[1] !== undefined) {
        const core = endTailRest[1].trimEnd();
        return (
          renderOeisFormulaPhysicalLine("(Start)") +
          (core ? renderOeisFormulaPhysicalLine(core) : "") +
          renderOeisFormulaPhysicalLine("(End)")
        );
      }
      const middleOnly = rest.trim();
      return (
        renderOeisFormulaPhysicalLine("(Start)") + (middleOnly ? renderOeisFormulaPhysicalLine(middleOnly) : "")
      );
    }
  }

  const t = norm.trim();
  if (!t) return "";
  if (/^\[?\s*table\s+from\b/i.test(t)) {
    return (
      `<div class="npp-oeis-formula-line npp-oeis-formula-line--marker npp-oeis-formula-line--marker-from">` +
      `${formatOeisAuthorLineHtml(t)}` +
      `</div>`
    );
  }
  const fromLead = splitOeisLeadingFromAttribution(t);
  if (fromLead.fromLine) {
    const fromHtml =
      `<div class="npp-oeis-formula-line npp-oeis-formula-line--marker npp-oeis-formula-line--marker-from">` +
      `${formatOeisFromAttributionHtml(fromLead.fromLine)}` +
      `</div>`;
    const rest = fromLead.body.trim();
    if (!rest) return fromHtml;
    return fromHtml + renderOeisFormulaPhysicalLine(rest);
  }
  if (/^\(start\)$/i.test(t)) {
    return `<div class="npp-oeis-formula-line npp-oeis-formula-line--marker npp-oeis-formula-line--marker-start">(Start)</div>`;
  }
  if (/^\(end\)$/i.test(t)) {
    return `<div class="npp-oeis-formula-line npp-oeis-formula-line--marker npp-oeis-formula-line--marker-end">(End)</div>`;
  }
  const endTail = t.match(/^(.*?)(?:\.)?\s*\(end\)\s*\.?\s*$/i);
  if (endTail?.[1] !== undefined) {
    const core = endTail[1].trimEnd();
    const endMarker = `<div class="npp-oeis-formula-line npp-oeis-formula-line--marker npp-oeis-formula-line--marker-end">(End)</div>`;
    if (!core) return endMarker;
    return renderOeisFormulaPhysicalLine(core) + endMarker;
  }
  const { label, body } = splitOeisFormulaLabelLine(t);
  const mathSrc = label ? body : t;
  if (!label && isLikelyOeisPlainDashByline(mathSrc)) {
    return (
      `<div class="npp-oeis-formula-line npp-oeis-formula-line--marker npp-oeis-formula-line--marker-from">` +
      `${formatOeisAuthorLineHtml(mathSrc)}` +
      `</div>`
    );
  }
  if (isLikelyOeisFormulaSentenceText(mathSrc)) {
    if (label) {
      const bodyRich = oeisFormulaFallbackRichHtml(body);
      return (
        `<div class="npp-oeis-formula-line">` +
        `<span class="npp-oeis-formula-k">${escapeHtml(label)}:</span>` +
        `<span class="npp-oeis-formula-v npp-oeis-formula-v--rich npp-oeis-math-target">` +
        `<span class="npp-oeis-formula-fallback">${bodyRich}</span></span>` +
        `</div>`
      );
    }
    const rich = oeisFormulaFallbackRichHtml(t);
    return (
      `<div class="npp-oeis-formula-line npp-oeis-formula-line--text npp-oeis-math-target">` +
      `<span class="npp-oeis-formula-fallback">${rich}</span>` +
      `</div>`
    );
  }
  if ((hasFormulaMathSignal(mathSrc) || hasFormulaQuantifierCue(mathSrc) || hasFormulaIffCue(mathSrc)) && hasFormulaCueWords(mathSrc)) {
    const mixedInner = renderOeisMixedFormulaInner(mathSrc);
    if (mixedInner) {
      if (label) {
        return (
          `<div class="npp-oeis-formula-line">` +
          `<span class="npp-oeis-formula-k">${escapeHtml(label)}:</span>` +
          `<span class="npp-oeis-formula-v npp-oeis-formula-v--rich npp-oeis-math-target">${mixedInner}</span>` +
          `</div>`
        );
      }
      return `<div class="npp-oeis-formula-line npp-oeis-formula-line--text npp-oeis-math-target">${mixedInner}</div>`;
    }
  }
  if (!label && hasFormulaMathSignal(mathSrc) && !isLikelyOeisFormulaMathSource(mathSrc)) {
    const mixed = renderOeisMixedFormulaLine(mathSrc);
    if (mixed) return mixed;
  }
  const mathBlock = isLikelyOeisFormulaMathSource(mathSrc) ? renderOeisFormulaMathSegments(mathSrc) : "";
  if (!mathBlock) {
    if (label) {
      const bodyRich = oeisFormulaFallbackRichHtml(body);
      return (
        `<div class="npp-oeis-formula-line">` +
        `<span class="npp-oeis-formula-k">${escapeHtml(label)}:</span>` +
        `<span class="npp-oeis-formula-v npp-oeis-formula-v--rich npp-oeis-math-target">` +
        `<span class="npp-oeis-formula-fallback">${bodyRich}</span></span>` +
        `</div>`
      );
    }
    const rich = oeisFormulaFallbackRichHtml(t);
    return (
      `<div class="npp-oeis-formula-line npp-oeis-formula-line--text npp-oeis-math-target">` +
      `<span class="npp-oeis-formula-fallback">${rich}</span>` +
      `</div>`
    );
  }
  if (label) {
    return (
      `<div class="npp-oeis-formula-line">` +
      `<span class="npp-oeis-formula-k">${escapeHtml(label)}:</span>` +
      `<span class="npp-oeis-formula-v">${mathBlock}</span>` +
      `</div>`
    );
  }
  return `<div class="npp-oeis-formula-line npp-oeis-formula-line--display">${mathBlock}</div>`;
}

/** One JSON `formula` string → HTML (may be multiple visual lines + optional attribution). */
export function formatOeisFormulaItemHtml(raw: string): string {
  const { core, author, tail } = stripOeisFormulaAttribution(raw);
  const normalized = core.replace(/\r\n/g, "\n").trim();
  /* Blank-line–separated paragraphs only; single newlines stay inside one chunk for math rows. */
  const chunks = normalized.length ? normalized.split(/\n\n+/).map((c) => c.trim()).filter((c) => c.length > 0) : [];
  const blocks = chunks.map((c) => renderOeisFormulaPhysicalLine(c)).filter((h) => h.length > 0);
  const inner = blocks.length > 0 ? blocks.join("") : `<div class="npp-oeis-formula-line npp-oeis-formula-line--text"></div>`;
  let sig = "";
  if (author !== null && tail !== null) {
    sig = `<footer class="npp-oeis-formula-sig"><em>${escapeHtml(author)}</em>, ${escapeHtml(tail)}</footer>`;
  }
  return `<article class="npp-oeis-formula-card">${inner}${sig}</article>`;
}

function isAsciiTableBorderLine(s: string): boolean {
  const t = s.trim();
  if (!t) return false;
  return /^[\-=]{8,}$/.test(t) || /^[\-= ]{8,}$/.test(t);
}

function isAsciiTableRowLine(s: string): boolean {
  const barCount = (s.match(/\|/g) || []).length;
  if (barCount < 2) return false;
  const t = s.trim();
  /* Guard against absolute-value notation like |mu(n)| in formulas:
   * require the line to start/end with | (classic bordered table) OR
   * have | with whitespace on BOTH sides (column separator). Single-side
   * whitespace like "1} |mu(n)|" is absolute-value, not a table column. */
  return t.startsWith("|") || t.endsWith("|") || /\s\|\s/.test(s);
}

function isAsciiSpaceColumnsLine(s: string): boolean {
  const cols = splitAsciiSpaceColumns(s);
  if (cols.length < 2) return false;
  if (cols.every((c) => /^-+$/.test(c))) return true;
  return cols.some((c) => /[A-Za-z0-9]/.test(c));
}

function isLikelyAsciiTableLine(s: string): boolean {
  return isAsciiTableRowLine(s) || isAsciiTableBorderLine(s) || isAsciiSpaceColumnsLine(s);
}

/** Group adjacent ASCII-table lines inside `formula` and render as one rich table block. */
function renderOeisFormulaItemsHtml(items: readonly string[]): string {
  const out: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const cur = String(items[i] ?? "");
    if (isLikelyAsciiTableLine(cur)) {
      const block: string[] = [cur];
      let j = i + 1;
      while (j < items.length && isLikelyAsciiTableLine(String(items[j] ?? ""))) {
        block.push(String(items[j] ?? ""));
        j++;
      }
      const merged = block.join("\n");
      const table = tryOeisTextTableToHtml(merged);
      if (table) {
        out.push(
          `<li class="npp-oeis-formula-li npp-oeis-rich npp-oeis-math-target">` +
            `<div class="npp-oeis-rich-body npp-oeis-formula-ascii-table">${table}</div>` +
            `</li>`,
        );
      } else {
        out.push(`<li class="npp-oeis-formula-li">${formatOeisFormulaItemHtml(merged)}</li>`);
      }
      i = j - 1;
      continue;
    }
    out.push(`<li class="npp-oeis-formula-li">${formatOeisFormulaItemHtml(cur)}</li>`);
  }
  return out.join("");
}

function sectionRichList(title: string, items: string[]): string {
  if (!items.length) return "";
  const lis = items
    .map(
      (raw) =>
        `<li class="npp-oeis-rich-li npp-oeis-rich npp-oeis-math-target"><div class="npp-oeis-rich-body">${oeisPlainTextToRichHtml(raw)}</div></li>`,
    )
    .join("");
  return `<section class="npp-oeis-section"><h3>${escapeHtml(title)}</h3><ul class="npp-oeis-rich-list">${lis}</ul></section>`;
}

export type OeisProgramKind = "maple" | "mathematica" | "generic";

const HL_SUBSET_MAPLE = ["perl", "python", "lua", "r", "ruby", "javascript"] as const;
const HL_SUBSET_MATHEMATICA = ["ruby", "javascript", "python", "lua", "perl", "r"] as const;
const HL_SUBSET_GENERIC = ["python", "bash", "c", "perl", "lua", "r", "javascript", "cpp"] as const;

/** highlight.js auto-detect within CAS-friendly language set (Maple / WL have no built-in grammars). */
export function oeisHighlightProgramCode(code: string, kind: OeisProgramKind): { value: string; language: string | null } {
  const trimmed = code.trim();
  if (!trimmed) return { value: "", language: null };
  const subset =
    kind === "maple"
      ? [...HL_SUBSET_MAPLE]
      : kind === "mathematica"
        ? [...HL_SUBSET_MATHEMATICA]
        : [...HL_SUBSET_GENERIC];
  try {
    const r = hljs.highlightAuto(trimmed, subset);
    return { value: r.value, language: r.language ?? null };
  } catch {
    return { value: escapeHtml(trimmed), language: null };
  }
}

/** One program snippet → collapsible `<details>` + highlighted `<pre><code>`. */
export function formatOeisProgramCodeBlockHtml(code: string, kind: OeisProgramKind): string {
  const normalized = code.replace(/\r\n/g, "\n");
  const lines = normalized.length ? normalized.split("\n") : [""];
  const n = lines.length;
  const { value, language } = oeisHighlightProgramCode(normalized, kind);
  const langLabel = kind === "maple" ? "Maple" : kind === "mathematica" ? "Wolfram Language" : "Program";
  const detectedMeta = language ? ` · ${escapeHtml(language)}` : "";
  const openAttr = n > 18 ? "" : " open";
  const langClass = language ? `language-${language}` : "npp-plaintext";
  return (
    `<details class="npp-oeis-codeblock"${openAttr}>` +
    `<summary class="npp-oeis-codeblock-sum"><span class="npp-oeis-codeblock-sum-title">${escapeHtml(langLabel)}</span>` +
    `<span class="npp-oeis-codeblock-sum-meta">${n} line${n === 1 ? "" : "s"}${detectedMeta}</span></summary>` +
    `<pre class="npp-oeis-code-pre" tabindex="0"><code class="hljs ${langClass}">${value}</code></pre>` +
    `</details>`
  );
}

function sectionProgramBlocks(title: string, items: string[], kind: OeisProgramKind): string {
  const nonempty = items.map((s) => String(s)).filter((s) => s.trim().length > 0);
  if (!nonempty.length) return "";
  const merged = nonempty.join("\n\n");
  const block = formatOeisProgramCodeBlockHtml(merged, kind);
  return `<section class="npp-oeis-section npp-oeis-section--code"><h3>${escapeHtml(title)}</h3>${block}</section>`;
}

function sectionLinkHtml(title: string, items: string[]): string {
  if (!items.length) return "";
  const blocks = items
    .map((h) => `<div class="npp-oeis-linkblock npp-oeis-rich npp-oeis-math-target">${sanitizeOeisLinkHtml(h)}</div>`)
    .join("");
  return `<section class="npp-oeis-section"><h3>${escapeHtml(title)}</h3>${blocks}</section>`;
}

/** Build sanitized HTML for the OEIS preview pane (no network). */
export function formatOeisEntryHtml(anumber: string, entry: OeisJsonEntry): string {
  const title = escapeHtml(entry.name ?? "(no name)");
  const data = entry.data ?? "";
  const terms = parseTermsData(data);
  const spark = termsSparklineSvg(terms);
  const formulas = fullStringList(entry.formula);
  const examples = fullStringList(entry.example);
  const comments = fullStringList(entry.comment);
  const keywords = fullStringList(entry.keyword);
  const xrefs = fullStringList(entry.xref);
  const links = fullStringList(entry.link);
  const references = [...fullStringList(entry.reference), ...fullStringList(entry.references)];
  const maple = fullStringList(entry.maple).filter((s) => !isLikelyOeisCasCrossrefProse(s));
  const mathematica = fullStringList(entry.mathematica).filter((s) => !isLikelyOeisCasCrossrefProse(s));
  const programs = fullStringList(entry.program);
  const offsets = fullStringList(entry.offset);
  const authors = fullStringList(entry.author);

  const metaId = entry.id ? escapeHtml(String(entry.id)) : "";
  const metaNum = entry.number !== undefined ? escapeHtml(String(entry.number)) : "";
  const metaCreated = entry.created ? escapeHtml(entry.created) : "";
  const metaTime = entry.time ? escapeHtml(entry.time) : "";
  const metaRev = entry.revision !== undefined ? escapeHtml(String(entry.revision)) : "";
  const oeisUrl = `https://oeis.org/${escapeHtml(anumber)}`;

  const metaBits: string[] = [];
  if (metaId) metaBits.push(`Internal id: ${metaId}`);
  if (metaNum) metaBits.push(`Sequence #: ${metaNum}`);
  if (offsets.length) metaBits.push(`Offset: ${escapeHtml(offsets.join("; "))}`);
  if (metaCreated) metaBits.push(`Created: ${metaCreated}`);
  if (metaTime) metaBits.push(`Time: ${metaTime}`);
  if (metaRev) metaBits.push(`Revision: ${metaRev}`);

  const parts: string[] = [];
  parts.push(`<header class="npp-oeis-header"><h2 class="npp-oeis-title">${escapeHtml(anumber)}</h2>`);
  parts.push(`<p class="npp-oeis-name">${title}</p>`);
  if (metaBits.length) {
    parts.push(`<p class="npp-oeis-meta">${metaBits.join(" · ")}</p>`);
  }
  const authorRows = expandOeisAuthorEntries(authors);
  if (authorRows.length) {
    const lis = authorRows.map((a) => `<li class="npp-oeis-author-item">${formatOeisAuthorLineHtml(a)}</li>`).join("");
    parts.push(
      `<div class="npp-oeis-authors">` +
        `<h3 class="npp-oeis-authors-heading">Authors</h3>` +
        `<ul class="npp-oeis-author-list">${lis}</ul>` +
        `</div>`,
    );
  }
  parts.push(
    `<p class="npp-oeis-link"><a class="npp-oeis-external-browser" href="${oeisUrl}" target="_blank" rel="noopener noreferrer">Open on oeis.org</a></p></header>`,
  );

  if (spark) {
    parts.push(
      `<section class="npp-oeis-section"><h3>Terms (first ${Math.min(terms.length, 160)} of ${terms.length})</h3>${spark}</section>`,
    );
  }
  if (data) {
    parts.push(
      `<section class="npp-oeis-section"><h3>Data</h3><pre class="npp-oeis-data" tabindex="0">${escapeHtml(data)}</pre></section>`,
    );
  }
  if (formulas.length) {
    const lis = renderOeisFormulaItemsHtml(formulas);
    parts.push(`<section class="npp-oeis-section"><h3>Formula</h3><ul class="npp-oeis-formula-list">${lis}</ul></section>`);
  }
  parts.push(sectionOeisMarkdownProse("Example", examples, "npp-oeis-section--example"));
  parts.push(sectionOeisMarkdownProse("Comments", comments, "npp-oeis-section--comment"));
  parts.push(sectionProgramBlocks("Maple", maple, "maple"));
  parts.push(sectionProgramBlocks("Mathematica", mathematica, "mathematica"));
  parts.push(sectionProgramBlocks("Program", programs, "generic"));
  parts.push(sectionLinkHtml("Links", links));
  parts.push(sectionRichList("References", references));
  if (keywords.length) {
    const joined = escapeHtml(keywords.join(", "));
    parts.push(`<section class="npp-oeis-section"><h3>Keywords</h3><p class="npp-oeis-keywords">${joined}</p></section>`);
  }
  if (xrefs.length) {
    const body = xrefs.map((x) => oeisPlainTextToRichHtml(x)).join(" ");
    parts.push(
      `<section class="npp-oeis-section"><h3>Crossrefs</h3><div class="npp-oeis-rich npp-oeis-math-target npp-oeis-xref">${body}</div></section>`,
    );
  }

  return `<div class="npp-oeis-doc">${parts.join("")}</div>`;
}

export type OeisErrorOptions = {
  /** If the error is recoverable by retrying (e.g. 5xx), show a hint. */
  retryHint?: boolean;
  /** When we know the A-number, offer a direct oeis.org link. */
  oeisUrl?: string;
};

export function formatOeisErrorHtml(message: string, opts?: OeisErrorOptions): string {
  const parts: string[] = [`<p>${escapeHtml(message)}</p>`];
  if (opts?.retryHint) {
    parts.push(
      `<p class="npp-oeis-error-hint">This is usually a temporary OEIS server issue — edit the A-number or wait a moment and it will retry automatically.</p>`,
    );
  }
  if (opts?.oeisUrl) {
    const safeUrl = escapeHtml(opts.oeisUrl);
    parts.push(
      `<p class="npp-oeis-error-link"><a href="${safeUrl}" target="_blank" rel="noopener noreferrer">View on oeis.org ↗</a></p>`,
    );
  }
  return `<div class="npp-oeis-error">${parts.join("")}</div>`;
}

export function formatOeisLoadingHtml(): string {
  return `<div class="npp-oeis-loading"><p>Loading OEIS…</p></div>`;
}

export function formatOeisHintHtml(): string {
  return (
    `<div class="npp-oeis-hint"><p>Enter an OEIS A-number on the first line (e.g. <code>A000045</code>), paste a link such as <code>https://oeis.org/A000045</code>, or put at least three signed integers on the first line (e.g. <code>1, 2, 3, 5, 8</code>) to search by terms. The preview updates automatically.</p></div>`
  );
}

export type OeisSearchPickListOptions = {
  /** When the last OEIS page was full (10 rows), offer in-app pagination. */
  showLoadMore?: boolean;
};

/**
 * Multiple OEIS hits for a lookup-by-terms query: pick a row to load `id:A…` into the buffer.
 * Buttons use class `npp-oeis-search-hit` and `data-oeis-id` with canonical `A000045`.
 */
export function formatOeisSearchPickListHtml(
  query: string,
  rows: readonly OeisJsonEntry[],
  opts?: OeisSearchPickListOptions,
): string {
  const showLoadMore = opts?.showLoadMore === true;
  const usable = rows
    .map((row) => ({ row, id: oeisAnumberFromSearchHit(row) }))
    .filter((x): x is { row: OeisJsonEntry; id: string } => x.id !== null);
  if (!usable.length) {
    return formatOeisErrorHtml(`No sequences matched terms ${query}.`);
  }
  const qEsc = escapeHtml(query);
  const lis = usable
    .map(({ row, id }) => {
      const title = typeof row.name === "string" ? row.name.trim() : "";
      const dataRaw = typeof row.data === "string" ? row.data.trim() : "";
      const dataPreview = dataRaw ? dataRaw.slice(0, 120) + (dataRaw.length > 120 ? "…" : "") : "";
      const titleHtml = title ? escapeHtml(title) : "";
      const bits: string[] = [];
      bits.push(`<span class="npp-oeis-search-hit-id">${escapeHtml(id)}</span>`);
      if (titleHtml) bits.push(`<span class="npp-oeis-search-hit-name">${titleHtml}</span>`);
      if (dataPreview) bits.push(`<code class="npp-oeis-search-hit-data">${escapeHtml(dataPreview)}</code>`);
      return (
        `<li class="npp-oeis-search-hit-item">` +
        `<button type="button" class="npp-oeis-search-hit" data-oeis-id="${escapeHtml(id)}">` +
        bits.join("") +
        `</button></li>`
      );
    })
    .join("");
  const loadMore = showLoadMore
    ? `<p class="npp-oeis-search-pick-more"><button type="button" class="npp-md-toolbar-btn npp-oeis-search-load-more">Load more results</button></p>`
    : "";
  return (
    `<div class="npp-oeis-search-pick">` +
    `<p class="npp-oeis-search-pick-intro">OEIS sequences matching <code>${qEsc}</code>. Pick one to load into the editor (fetches full entry). Use <strong>Load more results</strong> to append the next page from OEIS here in NullaPad.</p>` +
    `<ul class="npp-oeis-search-hit-list">${lis}</ul>` +
    loadMore +
    `</div>`
  );
}